import type { ModelInfo } from "./types";
import { buildManagedModelSummaries } from "../../../../../src/shared/inference-provider-registry.js";

function buildFallbackModelsResponse(): { models: ModelInfo[] } {
  return {
    models: buildManagedModelSummaries().map((model) => ({
      id: model.id,
      name: model.name,
      enabled: model.enabled,
      costPerInputToken: model.costPerInputToken,
      costPerOutputToken: model.costPerOutputToken,
    })),
  };
}

export async function fetchModels(): Promise<{ models: ModelInfo[] }> {
  try {
    const res = await fetch("/api/models");
    if (res.ok) {
      const data = await res.json();
      if (data.models && data.models.length > 0) return data;
    }
  } catch {
    // Fall through to defaults
  }
  return buildFallbackModelsResponse();
}

export async function approveApprovalItem(
  id: string,
  notes?: string,
  type?: string,
  edits?: unknown,
): Promise<void> {
  // Phase 1: proposal_card payloads route to the Work Ledger surface and
  // pass structured edits in the request body. Other approval types stay on
  // the legacy endpoint.
  if (type === "proposal_card") {
    await fetch(`/api/proposals/${id}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paymentEdits: edits ?? undefined }),
    });
    return;
  }
  await fetch(`/api/approvals/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reviewedBy: "operator", reviewNotes: notes }),
  });
}

export async function rejectApprovalItem(id: string, notes?: string, type?: string): Promise<void> {
  if (type === "proposal_card") {
    await fetch(`/api/proposals/${id}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: notes }),
    });
    return;
  }
  await fetch(`/api/approvals/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reviewedBy: "operator", reviewNotes: notes }),
  });
}

export async function executeProposalItem(id: string): Promise<void> {
  await fetch(`/api/methodology/proposals/${id}/execute`, { method: "POST" });
}

export async function undoProposalItem(id: string): Promise<void> {
  await fetch(`/api/methodology/proposals/${id}/undo`, { method: "POST" });
}

export async function submitFeedback(
  messageId: string,
  feedback: "positive" | "negative",
): Promise<void> {
  try {
    await fetch("/api/chat/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messageId, feedback }),
    });
  } catch {
    // Store locally as fallback
  }
}

/**
 * Known starter tag set for the inline proposal-feedback widget.
 *
 * Mirrors EXPLICIT_FEEDBACK_TAG_TRAIT_MAP in
 * extensions/operator-model/src/learning/explicit-feedback-tag-map.ts —
 * re-declared here to avoid a UI → extensions/ import boundary cross.
 * Keep in sync; the canonical map IS the routing truth (unknown tags
 * are silently dropped by the consumer end, so adding a tag here without
 * a matching map entry is a no-op rather than a runtime error).
 */
export const KNOWN_PROPOSAL_FEEDBACK_TAGS = [
  "too_formal",
  "too_casual",
  "too_long",
  "too_short",
  "too_technical",
] as const;

export type ProposalFeedbackTag = (typeof KNOWN_PROPOSAL_FEEDBACK_TAGS)[number];

/**
 * Post operator feedback on an executed proposal.
 *
 * The producer route (`POST /api/proposals/:id/feedback`,
 * `hank/src/api/routes/proposals.ts:1028+`) accepts:
 *   { rating: "positive" | "negative", tags?: string[], comment?: string }
 *
 * All discriminants (tenantId, operatorId, ledgerId, templateId) are
 * resolved server-side from the URL param + auth headers — body never
 * carries them.
 *
 * Errors are swallowed (mirror of submitFeedback). The widget renders
 * an optimistic confirmation state; an SSE refresh covers eventual
 * server-side state.
 */
export async function submitProposalFeedback(
  id: string,
  rating: "positive" | "negative",
  tags?: string[],
  comment?: string,
): Promise<void> {
  try {
    const body: Record<string, unknown> = { rating };
    if (tags && tags.length > 0) body.tags = tags;
    if (comment && comment.length > 0) body.comment = comment;
    await fetch(`/api/proposals/${id}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    // SSE refresh will reconcile if needed; widget shows optimistic state.
  }
}
